import { action, ClassModel, register, types, extend, SnapshotIn } from "@gadgetinc/mobx-quick-tree";
import { EnvironmentType } from "./Environment";
import { LegacyEnvironmentApiKey } from "./EnvironmentApiKey";
import { ApiKeyProblems } from "./problems/finders/ApiKeyProblems";
import type { ProblemsList } from "./problems/ProblemsList";
import { contentHash, GadgetDomainModelClass, getAppSettings } from "./utils";
import { apiKeyGenerator } from "./apiKeyGenerator";
import type { AppSettings } from "./AppSettings";
import { merge, omit } from "lodash";

const BaseApiKey = (name: string) => {
  class BaseApiKey extends GadgetDomainModelClass(name, {
    name: types.string,
    roles: types.array(types.string),
  }) {
    @action
    setRoles(roles: string[]) {
      this.roles.replace(roles);
    }
    @action
    setName(name: string) {
      this.name = name;
    }
  }

  return BaseApiKey;
};

@register
export class ApiKey extends extend(BaseApiKey("MultiEnvApiKey"), {
  secretKey: types.optional(types.string, apiKeyGenerator),
}) {
  get contentHash() {
    return contentHash([this.name, this.secretKey, ...this.roles]);
  }

  get problems(): ProblemsList {
    return ApiKeyProblems(this);
  }

  get allProblems() {
    return this.problems.allProblemsWithReferenceNode(this);
  }

  @action
  setSecretKey(key: string) {
    this.secretKey = key;
  }
}

@register
export class ApiKeyValue extends ClassModel({
  Development: types.optional(LegacyEnvironmentApiKey, {}),
  Production: types.optional(LegacyEnvironmentApiKey, {}),
}) {}

@register
export class LegacyApiKey extends extend(BaseApiKey("ApiKey"), {
  secretKeys: types.optional(
    types.late(() => ApiKeyValue),
    { Development: {}, Production: {} }
  ),
}) {
  get contentHash() {
    return contentHash([this.name, this.secretKeys.Development.contentHash, this.secretKeys.Production.contentHash, ...this.roles]);
  }

  get secretKey() {
    const environmentType = getAppSettings(this).environmentType;
    return environmentType == EnvironmentType.Production ? this.secretKeys.Production.secretKey : this.secretKeys.Development.secretKey;
  }

  get problems(): ProblemsList {
    return ApiKeyProblems(this);
  }

  get allProblems() {
    return this.problems.allProblemsWithReferenceNode(this);
  }
}

export const AllApiKeysUnion = types.union(ApiKey, LegacyApiKey);
export type AllApiKeys = ApiKey | LegacyApiKey;
export const getApiKeyAttrsForSettings = (settings: AppSettings, attrs: Partial<SnapshotIn<typeof AllApiKeysUnion>> = {}) => {
  if (settings.multiEnvironmentEnabled) {
    return {
      secretKey: apiKeyGenerator(),
      ...omit(attrs, ["secretKeys", "type"]),
      type: "MultiEnvApiKey",
    };
  } else {
    return {
      secretKeys: merge(
        {
          Development: { secretKey: apiKeyGenerator() },
          Production: { secretKey: apiKeyGenerator() },
        },
        "secretKeys" in attrs ? attrs?.secretKeys : {}
      ),
      ...omit(attrs, ["secretKeys", "type"]),
      type: "ApiKey",
    };
  }
};
